import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Music, Users, Heart, XCircle, Download, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEAL_LABELS = { beef: "Beef", fish: "Fish", vegetarian: "Vegetarian" };
const EVENT_LABELS = { welcome_party: "Welcome Party", wedding: "Wedding", brunch: "Brunch" };

export default function Admin() {
  const [rsvps, setRsvps] = useState(null);
  const [guests, setGuests] = useState([]);
  const [gp, setGp] = useState({ party_name: "", members: "", seats: 2 });

  const loadGuests = () => axios.get(`${API}/guests`).then((r) => setGuests(r.data)).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/rsvp`).then((r) => setRsvps(r.data)).catch(() => setRsvps([]));
    loadGuests();
  }, []);

  const addParty = async (e) => {
    e.preventDefault();
    const members = gp.members.split(",").map((m) => m.trim()).filter(Boolean);
    if (!gp.party_name.trim() || members.length === 0) return;
    await axios.post(`${API}/guests`, { party_name: gp.party_name.trim(), members, seats: Number(gp.seats) || members.length });
    setGp({ party_name: "", members: "", seats: 2 });
    loadGuests();
  };

  const delParty = async (id) => {
    await axios.delete(`${API}/guests/${id}`);
    loadGuests();
  };

  if (!rsvps)
    return (
      <div className="min-h-screen bg-[#FAFAF1] flex items-center justify-center font-serif-display text-2xl text-[#465220]">
        Loading…
      </div>
    );

  const yes = rsvps.filter((r) => r.attending === "yes");
  const no = rsvps.filter((r) => r.attending !== "yes");
  const totalGuests = yes.reduce((s, r) => s + (r.guest_count || 1), 0);
  const mealCounts = {};
  yes.forEach((r) => {
    if (r.attendees?.length) r.attendees.forEach((a) => { if (a.meal) mealCounts[a.meal] = (mealCounts[a.meal] || 0) + 1; });
    else if (r.meal) mealCounts[r.meal] = (mealCounts[r.meal] || 0) + 1;
  });
  const eventCounts = {};
  yes.forEach((r) => (r.events || []).forEach((e) => { eventCounts[e] = (eventCounts[e] || 0) + (r.guest_count || 1); }));
  const songs = rsvps.filter((r) => r.song_request);

  const stats = [
    { icon: Heart, label: "Attending Parties", value: yes.length, testid: "admin-stat-attending" },
    { icon: Users, label: "Total Guests", value: totalGuests, testid: "admin-stat-guests" },
    { icon: XCircle, label: "Declined", value: no.length, testid: "admin-stat-declined" },
    { icon: Music, label: "Song Requests", value: songs.length, testid: "admin-stat-songs" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF1] text-[#2C3552] px-6 py-16" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.35em] text-[#465220] mb-3">Private · Paulina &amp; Louie</p>
            <h1 className="font-serif-display font-light text-4xl sm:text-5xl">
              RSVP <span className="italic text-[#465220]">Dashboard</span>
            </h1>
          </div>
          <div className="flex items-center gap-6">
            <a
              data-testid="admin-export-btn"
              href={`${API}/rsvp/export/csv`}
              download
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] px-6 py-3 rounded-full bg-[#2C3552] text-[#FAFAF1] hover:bg-[#465220] transition-colors duration-300"
            >
              <Download size={14} /> Export CSV
            </a>
            <Link
              data-testid="admin-back-link"
              to="/"
              className="text-[11px] uppercase tracking-[0.25em] text-[#465220] border-b border-[#465220]/40 pb-1 hover:border-[#465220] transition-colors duration-300"
            >
              ← Back to site
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {stats.map((s) => (
            <div key={s.label} data-testid={s.testid} className="border border-[#DFE4D0] bg-white/60 p-6 sm:p-8">
              <s.icon size={18} className="text-[#A9B08C]" />
              <p className="font-serif-display font-light text-4xl sm:text-5xl mt-4 text-[#465220]">{s.value}</p>
              <p className="text-[10px] uppercase tracking-[0.25em] mt-2 text-[#2C3552]/50">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="border border-[#DFE4D0] bg-white/60 p-6 sm:p-8" data-testid="admin-meal-counts">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#465220] mb-5">Meal Counts</p>
            {Object.keys(MEAL_LABELS).map((m) => (
              <div key={m} className="flex justify-between py-2.5 border-b border-[#DFE4D0] last:border-0">
                <span className="font-serif-display text-lg">{MEAL_LABELS[m]}</span>
                <span className="font-serif-display text-lg text-[#465220]">{mealCounts[m] || 0}</span>
              </div>
            ))}
          </div>
          <div className="border border-[#DFE4D0] bg-white/60 p-6 sm:p-8" data-testid="admin-event-counts">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#465220] mb-5">Guests per Event</p>
            {Object.keys(EVENT_LABELS).map((e) => (
              <div key={e} className="flex justify-between py-2.5 border-b border-[#DFE4D0] last:border-0">
                <span className="font-serif-display text-lg">{EVENT_LABELS[e]}</span>
                <span className="font-serif-display text-lg text-[#465220]">{eventCounts[e] || 0}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-[#DFE4D0] bg-white/60 p-6 sm:p-8 mt-4" data-testid="admin-guest-manager">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#465220] mb-2">Guest List — Find Your Invitation</p>
          <p className="text-sm font-light text-[#2C3552]/60 mb-6">
            Add each invitation party here. Guests will type their name on the RSVP form to find it. If the list is empty, the RSVP form stays open to everyone.
          </p>
          <form onSubmit={addParty} className="grid sm:grid-cols-[1fr_1.5fr_100px_auto] gap-4 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#2C3552]/50">Party name</label>
              <input data-testid="admin-guest-name-input" value={gp.party_name} onChange={(e) => setGp({ ...gp, party_name: e.target.value })} placeholder="The Rascon Family" className="editorial-input w-full mt-1 py-2 text-[#2C3552]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#2C3552]/50">Members (comma-separated)</label>
              <input data-testid="admin-guest-members-input" value={gp.members} onChange={(e) => setGp({ ...gp, members: e.target.value })} placeholder="Ana Rascon, Luis Rascon" className="editorial-input w-full mt-1 py-2 text-[#2C3552]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#2C3552]/50">Seats</label>
              <input data-testid="admin-guest-seats-input" type="number" min="1" value={gp.seats} onChange={(e) => setGp({ ...gp, seats: e.target.value })} className="editorial-input w-full mt-1 py-2 text-[#2C3552]" />
            </div>
            <button data-testid="admin-guest-add-btn" type="submit" className="text-[11px] uppercase tracking-[0.25em] px-6 py-2.5 rounded-full bg-[#465220] text-[#FAFAF1] hover:bg-[#2C3552] transition-colors duration-300">
              Add
            </button>
          </form>
          {guests.length > 0 && (
            <div className="mt-6 divide-y divide-[#DFE4D0]">
              {guests.map((g, i) => (
                <div key={g.id} data-testid={`admin-guest-row-${i}`} className="flex items-center justify-between gap-4 py-3">
                  <div>
                    <span className="font-serif-display text-lg">{g.party_name}</span>
                    <span className="text-sm font-light text-[#2C3552]/60 ml-3">{g.members.join(", ")} · {g.seats} seats</span>
                  </div>
                  <button data-testid={`admin-guest-delete-${i}`} onClick={() => delParty(g.id)} aria-label="Delete party" className="text-[#E15A70] hover:text-[#2C3552] transition-colors duration-300 p-1">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {songs.length > 0 && (
          <div className="border border-[#DFE4D0] bg-white/60 p-6 sm:p-8 mt-4" data-testid="admin-song-list">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#465220] mb-5">The Playlist</p>
            <div className="flex flex-wrap gap-3">
              {songs.map((r) => (
                <span key={r.id} className="text-sm font-light border border-[#A9B08C]/60 rounded-full px-4 py-1.5">
                  {r.song_request} <span className="text-[#2C3552]/40">— {r.full_name}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#465220] mb-5">
            All Responses ({rsvps.length})
          </p>
          {rsvps.length === 0 ? (
            <p className="font-serif-display italic text-xl text-[#2C3552]/50" data-testid="admin-empty">
              No RSVPs yet — they'll appear here as guests respond.
            </p>
          ) : (
            <div className="overflow-x-auto border border-[#DFE4D0] bg-white/60">
              <table className="w-full text-sm" data-testid="admin-table">
                <thead>
                  <tr className="border-b border-[#DFE4D0] text-left">
                    {["Name", "Email", "Attending", "Guests", "Meal", "Events", "Dietary", "Song", "Note"].map((h) => (
                      <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#2C3552]/50 font-medium whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rsvps.map((r, i) => (
                    <tr key={r.id} data-testid={`admin-row-${i}`} className="border-b border-[#DFE4D0] last:border-0 hover:bg-[#EEF2E3]/70 transition-colors duration-200">
                      <td className="px-4 py-3 font-serif-display text-base whitespace-nowrap">
                        {r.full_name}
                        {r.party_name && <span className="block text-xs font-sans font-light text-[#2C3552]/50">{r.party_name}</span>}
                      </td>
                      <td className="px-4 py-3 font-light text-[#2C3552]/70">{r.email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${r.attending === "yes" ? "bg-[#465220]/15 text-[#465220]" : "bg-[#2C3552]/10 text-[#2C3552]/60"}`}>
                          {r.attending === "yes" ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">{r.attending === "yes" ? r.guest_count : "—"}</td>
                      <td className="px-4 py-3">
                        {r.attendees?.length
                          ? r.attendees.map((a) => `${a.name}: ${MEAL_LABELS[a.meal] || "TBD"}`).join(", ")
                          : MEAL_LABELS[r.meal] || "—"}
                      </td>
                      <td className="px-4 py-3 font-light">{(r.events || []).map((e) => EVENT_LABELS[e]).join(", ") || "—"}</td>
                      <td className="px-4 py-3 font-light">{r.dietary || "—"}</td>
                      <td className="px-4 py-3 font-light">{r.song_request || "—"}</td>
                      <td className="px-4 py-3 font-light max-w-[220px]">{r.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
